import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { Button } from 'antd';
import { markNewBuildAvailable } from 'src/lib/buildState';
import { sendErrorEvent, trackEvent } from 'src/lib/googleAnalytics';
import { toast } from 'src/lib/toast';

/**
 * How often an open tab asks whether a newer build is live. Long on purpose: the point is that
 * a tab is never more than this far behind, not that it notices a deploy instantly.
 */
const POLL_INTERVAL = 15 * OneMinuteInMillis;

const RELOADED_FOR_KEY = 'racemap:reloaded-for-build';
const PROMPTED_FOR_KEY = 'racemap:prompted-for-build';
const CHUNK_RECOVERED_KEY = 'racemap:chunk-recovered-on-build';

/**
 * Reloads unless we already reloaded for the same `token` under `storageKey`.
 *
 * The API reports its own BUILD_VERSION rather than reading the HTML off disk, so the two can
 * drift apart permanently (a container restarted against a different .env, say). Reloading
 * would then never resolve the mismatch and the tab would reload forever. Remembering what we
 * already reloaded for turns that into a single wasted reload.
 *
 * The two callers get separate keys so they cannot clear each other's guard.
 *
 * Returns whether it actually reloaded, so callers can tell a real update from a blocked one.
 */
function reloadOnce(storageKey: string, token: string): boolean {
  if (sessionStorage.getItem(storageKey) === token) return false;

  sessionStorage.setItem(storageKey, token);
  window.location.reload();
  return true;
}

/**
 * Watches for a newer frontend build being deployed under an open tab.
 *
 * Passive surfaces (player, leaderboard, elevation chart) pass `auto` and reload themselves.
 * The backoffice does not: reloading mid-form loses unsaved work, so it prompts instead.
 *
 * A plain function rather than a hook because three of the four entry points are imperative
 * module-scope scripts with no component to hang an effect off.
 *
 * Returns a stop function.
 */
export function startBuildUpdateWatch({
  auto,
  host = '',
}: {
  auto: boolean;
  host?: string;
}): () => void {
  // The passive surfaces poll their CDN host, so the client cannot be a module-level singleton
  // on `window.location.origin` like everywhere else in the frontend.
  const apiClient = new RacemapAPIClient(host || window.location.origin, {
    includeCredentials: false,
  });

  const check = async () => {
    const { version } = await apiClient.getVersion();

    const own = window.__BUILD_VERSION__;
    // Local dev stamps no version on either side, and a one-sided empty value proves nothing.
    if (!version || !own || version === own) return;

    markNewBuildAvailable();
    trackEvent('build_update_detected', {
      current_version: own,
      latest_version: version,
      mode: auto ? 'auto' : 'prompt',
    });

    if (auto) {
      if (reloadOnce(RELOADED_FOR_KEY, version)) {
        trackEvent('build_update_reload', {
          reason: 'auto',
          from_version: own,
          to_version: version,
        });
      } else {
        // Reloading did not resolve the mismatch, so the API's BUILD_VERSION disagrees with the
        // HTML nginx serves. Nothing the client can do — but it must not stay invisible.
        trackEvent('build_update_guard_blocked', { version });
      }
      return;
    }

    // Same guard as reloadOnce, so a version we already prompted for does not nag every tick.
    if (sessionStorage.getItem(PROMPTED_FOR_KEY) === version) return;
    sessionStorage.setItem(PROMPTED_FOR_KEY, version);

    trackEvent('build_update_prompt_shown', { latest_version: version });

    toast.info(
      {
        title: 'New version available',
        message: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              trackEvent('build_update_reload', {
                reason: 'prompt_click',
                from_version: own,
                to_version: version,
              });
              window.location.reload();
            }}
          >
            Reload
          </Button>
        ),
      },
      { autoClose: false },
    );
  };

  // A check that cannot complete says nothing more than "we do not know yet", which is how the
  // watcher already treats a missing version. Swallowed rather than reported: an offline venue
  // screen would otherwise emit an event every tick, and an endpoint that is genuinely down is
  // visible in the API's own metrics. Unhandled, it would reject on every interval instead.
  const timer = setInterval(() => {
    check().catch((error) => console.warn('Could not check for a build update', error));
  }, POLL_INTERVAL);

  return () => clearInterval(timer);
}

/**
 * Recovers from a lazy route chunk that no longer resolves — the classic symptom of a tab that
 * outlived its deploy. Vite fires `vite:preloadError` when a dynamic import fails; without this
 * the user lands on the error page with no way out but a manual refresh.
 *
 * Returns an uninstall function.
 */
export function installChunkFailureRecovery(): () => void {
  const onPreloadError = (event: WindowEventMap['vite:preloadError']) => {
    event.preventDefault();

    // Report before reloading, never after: once we navigate this failure class disappears
    // from the numbers entirely. gtag sends events via sendBeacon, so it survives the unload.
    sendErrorEvent(event.payload, true, { recovery: 'preload_error' });

    // Keyed on the running build, not on a constant: sessionStorage survives the reload this
    // triggers, so a constant would let one recovery block every later deploy in this tab.
    if (reloadOnce(CHUNK_RECOVERED_KEY, window.__BUILD_VERSION__ ?? 'unknown')) {
      trackEvent('build_update_reload', { reason: 'preload_error' });
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
